import React, { useState, useEffect } from 'react';
import { campaignAPI, senderEmailAPI, recipientAPI, followupAPI, signatureAPI, previewAPI } from '../api';

function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [senderEmails, setSenderEmails] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showFollowupModal, setShowFollowupModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [previewContent, setPreviewContent] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    content: '',
    sender_email_id: '',
    signature_id: '',
    cc_emails: '',
    bcc_emails: '',
  });

  const [sendData, setSendData] = useState({
    recipients: '',
  });

  const [followupData, setFollowupData] = useState({
    is_enabled: false,
    interval_days: 7,
    max_followups: 3,
    subject_template: '',
    content_template: '',
    stop_on_reply: true,
    stop_on_connected: true,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [campaignsRes, sendersRes, recipientsRes, signaturesRes] = await Promise.all([
        campaignAPI.getAll(),
        senderEmailAPI.getAll(),
        recipientAPI.getAll({ per_page: 1000 }),
        signatureAPI.getAll(),
      ]);

      if (campaignsRes.data.success) setCampaigns(campaignsRes.data.data);
      if (sendersRes.data.success) {
        setSenderEmails(sendersRes.data.data);
        console.log('✅ 发件邮箱加载成功:', sendersRes.data.data.length, '个');
      }
      if (recipientsRes.data.success) setRecipients(recipientsRes.data.data);
      if (signaturesRes.data.success) {
        setSignatures(signaturesRes.data.data);
        console.log('✅ 签名加载成功:', signaturesRes.data.data.length, '个');
      }
    } catch (error) {
      console.error('❌ 加载数据失败:', error);
      setMessage({ type: 'error', text: '加载数据失败，请刷新页面重试' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        cc_emails: formData.cc_emails.split(',').map((e) => e.trim()).filter(Boolean),
        bcc_emails: formData.bcc_emails.split(',').map((e) => e.trim()).filter(Boolean),
        signature_id: formData.signature_id || null,
      };

      await campaignAPI.create(payload);
      setMessage({ type: 'success', text: '✅ 活动创建成功！' });
      setShowModal(false);
      setFormData({
        name: '',
        subject: '',
        content: '',
        sender_email_id: '',
        signature_id: '',
        cc_emails: '',
        bcc_emails: '',
      });
      loadData();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: '创建失败：' + (error.response?.data?.message || error.message) });
    }
  };

  const handleDuplicate = async (campaign) => {
    // 列表为轻量数据（不含正文），复制时拉取完整活动
    let full = campaign;
    try {
      const resp = await campaignAPI.getOne(campaign.id);
      full = resp.data.data || campaign;
    } catch (e) { /* 退回列表数据 */ }
    setFormData({
      name: `${full.name} (副本)`,
      subject: full.subject,
      content: full.content || '',
      sender_email_id: full.sender_email?.id || '',
      signature_id: full.signature?.id || full.signature_id || '',
      cc_emails: full.cc_emails ? full.cc_emails.join(', ') : '',
      bcc_emails: full.bcc_emails ? full.bcc_emails.join(', ') : '',
    });
    setShowModal(true);
    setMessage({ type: 'info', text: '已复制活动内容，请修改后保存' });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handlePreview = async () => {
    try {
      const response = await previewAPI.email(formData.content, formData.signature_id || null);
      if (response.data.success) {
        setPreviewContent(response.data.data.full_content);
        setShowPreviewModal(true);
      }
    } catch (error) {
      setMessage({ type: 'error', text: '预览失败：' + (error.response?.data?.message || error.message) });
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    
    // 防止重复提交
    if (sending) {
      return;
    }
    
    try {
      setSending(true);
      setMessage({ type: 'info', text: '正在发送邮件，请稍候...' });
      
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const recipientList = sendData.recipients
        .split('\n')
        .map((e) => e.trim())
        .filter(Boolean)
        .filter((email) => {
          // 过滤掉HTML标签和非邮箱格式的内容
          if (email.includes('<') || email.includes('>') || email.includes('http')) {
            console.warn('过滤掉无效内容:', email);
            return false;
          }
          if (!emailRegex.test(email)) {
            console.warn('过滤掉非邮箱格式:', email);
            return false;
          }
          return true;
        });

      if (recipientList.length === 0) {
        setMessage({ type: 'error', text: '请输入有效的邮箱地址（每行一个）' });
        setSending(false);
        return;
      }

      const response = await campaignAPI.send(selectedCampaign.id, recipientList);
      setMessage({ type: 'success', text: `✅ 邮件发送成功！已发送给 ${recipientList.length} 个收件人` });
      setShowSendModal(false);
      setSendData({ recipients: '' });
      setSelectedCampaign(null);
      loadData();
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message || '未知错误';
      setMessage({ type: 'error', text: `❌ 发送失败：${errorMsg}` });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } finally {
      setSending(false);
    }
  };

  const handleFollowupSubmit = async (e) => {
    e.preventDefault();
    try {
      await followupAPI.saveConfig(selectedCampaign.id, followupData);
      setMessage({ type: 'success', text: 'Follow-up配置保存成功！' });
      setShowFollowupModal(false);
      setSelectedCampaign(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: '保存失败：' + (error.response?.data?.message || error.message) });
    }
  };

  const openSendModal = (campaign) => {
    setSelectedCampaign(campaign);
    setShowSendModal(true);
  };

  const openFollowupModal = async (campaign) => {
    setSelectedCampaign(campaign);
    try {
      const response = await followupAPI.getConfig(campaign.id);
      if (response.data.success && response.data.data) {
        setFollowupData(response.data.data);
      } else {
        setFollowupData({
          is_enabled: false,
          interval_days: 7,
          max_followups: 3,
          subject_template: '',
          content_template: '',
          stop_on_reply: true,
          stop_on_connected: true,
        });
      }
    } catch (error) {
      console.error('加载Follow-up配置失败:', error);
    }
    setShowFollowupModal(true);
  };

  const openDetailModal = (campaign) => {
    setSelectedCampaign(campaign);
    setShowDetailModal(true);
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: { class: 'badge-secondary', text: '草稿' },
      sending: { class: 'badge-warning', text: '发送中' },
      completed: { class: 'badge-success', text: '已完成' },
      paused: { class: 'badge-info', text: '已暂停' },
    };
    const badge = badges[status] || badges.draft;
    return <span className={`badge ${badge.class}`}>{badge.text}</span>;
  };

  if (loading) {
    return <div>加载中...</div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1>营销活动</h1>
        <p>创建和管理您的邮件营销活动</p>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type}`}>
          {message.text}
        </div>
      )}

      {/* 数据状态提示 */}
      {!loading && (senderEmails.length === 0 || signatures.length === 0) && (
        <div className="alert alert-info">
          <strong>💡 提示：</strong>
          {senderEmails.length === 0 && ' 您还没有添加发件邮箱。'}
          {signatures.length === 0 && ' 您还没有创建邮件签名。'}
          <br/>
          {senderEmails.length === 0 && (
            <span>请先前往 <a href="/sender-emails" style={{ color: '#2980b9', fontWeight: 'bold' }}>✉️ 发件邮箱</a> 页面添加邮箱。</span>
          )}
          {signatures.length === 0 && (
            <span> 可前往 <a href="/signatures" style={{ color: '#2980b9', fontWeight: 'bold' }}>✍️ 邮件签名</a> 页面创建签名（可选）。</span>
          )}
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h2>活动列表</h2>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            + 创建营销活动
          </button>
        </div>

        {campaigns.length === 0 ? (
          <p style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            暂无营销活动，点击上方按钮创建一个
          </p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>活动名称</th>
                <th>主题</th>
                <th>发件人</th>
                <th>状态</th>
                <th>发送进度</th>
                <th>创建时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((campaign) => (
                <tr key={campaign.id}>
                  <td>{campaign.name}</td>
                  <td>{campaign.subject}</td>
                  <td>{campaign.sender_email?.email || '-'}</td>
                  <td>{getStatusBadge(campaign.status)}</td>
                  <td>
                    {campaign.sent_count}/{campaign.total_recipients}
                  </td>
                  <td>{new Date(campaign.created_at).toLocaleDateString()}</td>
                  <td>
                    <button
                      className="btn btn-info"
                      style={{ padding: '5px 10px', fontSize: '12px', marginRight: '5px' }}
                      onClick={() => openDetailModal(campaign)}
                    >
                      查看详情
                    </button>
                    <button
                      className="btn btn-secondary"
                      style={{ padding: '5px 10px', fontSize: '12px', marginRight: '5px' }}
                      onClick={() => handleDuplicate(campaign)}
                      title="复制此活动并创建新的"
                    >
                      📋 复制
                    </button>
                    {campaign.status === 'draft' && (
                      <>
                        <button
                          className="btn btn-success"
                          style={{ padding: '5px 10px', fontSize: '12px', marginRight: '5px' }}
                          onClick={() => openSendModal(campaign)}
                          disabled={sending}
                        >
                          发送
                        </button>
                        <button
                          className="btn btn-primary"
                          style={{ padding: '5px 10px', fontSize: '12px' }}
                          onClick={() => openFollowupModal(campaign)}
                          disabled={sending}
                        >
                          Follow-up
                        </button>
                      </>
                    )}
                    {campaign.status !== 'draft' && (
                      <button
                        className="btn btn-primary"
                        style={{ padding: '5px 10px', fontSize: '12px' }}
                        onClick={() => openFollowupModal(campaign)}
                      >
                        Follow-up配置
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 创建活动模态框 */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h2>创建营销活动</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>活动名称 *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="例如: 2024春季营销活动"
                  required
                />
              </div>

              <div className="form-group">
                <label>发件邮箱 *</label>
                <select
                  value={formData.sender_email_id}
                  onChange={(e) => setFormData({ ...formData, sender_email_id: e.target.value })}
                  required
                >
                  <option value="">请选择发件邮箱</option>
                  {senderEmails.map((email) => (
                    <option key={email.id} value={email.id}>
                      {email.email} {email.name && `(${email.name})`}
                    </option>
                  ))}
                </select>
                {senderEmails.length === 0 && (
                  <small style={{ display: 'block', marginTop: '5px', color: '#e74c3c', fontSize: '12px' }}>
                    ⚠️ 没有可用的发件邮箱，请先在"发件邮箱"页面添加
                  </small>
                )}
                <small style={{ display: 'block', marginTop: '5px', color: '#666', fontSize: '12px' }}>
                  💡 如需添加新邮箱，请前往左侧菜单 "✉️ 发件邮箱" 页面
                </small>
              </div>

              <div className="form-group">
                <label>邮件主题 *</label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="输入邮件主题"
                  required
                />
              </div>

              <div className="form-group">
                <label>邮件内容 * (支持HTML)</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="输入邮件内容，支持HTML格式"
                  style={{ minHeight: '200px' }}
                  required
                />
              </div>

              <div className="form-group">
                <label>邮件签名</label>
                <select
                  value={formData.signature_id}
                  onChange={(e) => setFormData({ ...formData, signature_id: e.target.value })}
                >
                  <option value="">不使用签名</option>
                  {signatures.map((sig) => (
                    <option key={sig.id} value={sig.id}>
                      {sig.name} {sig.is_default && '(默认)'}
                    </option>
                  ))}
                </select>
                {signatures.length === 0 ? (
                  <small style={{ display: 'block', marginTop: '5px', color: '#e74c3c', fontSize: '12px' }}>
                    ⚠️ 没有可用的签名。<a href="/signatures" style={{ color: '#3498db' }}>点击这里创建签名</a>
                  </small>
                ) : (
                  <small style={{ display: 'block', marginTop: '5px', color: '#666', fontSize: '12px' }}>
                    签名会自动附加到邮件内容末尾。💡 可在"✍️ 邮件签名"页面管理
                  </small>
                )}
              </div>

              <div style={{ marginBottom: '20px' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handlePreview}
                  style={{ marginRight: '10px' }}
                >
                  📄 预览邮件
                </button>
                <small style={{ color: '#666', fontSize: '12px' }}>
                  预览包含签名的完整邮件效果
                </small>
              </div>

              <div className="form-group">
                <label>抄送 (CC) - 用逗号分隔多个邮箱</label>
                <input
                  type="text"
                  value={formData.cc_emails}
                  onChange={(e) => setFormData({ ...formData, cc_emails: e.target.value })}
                  placeholder="cc1@example.com, cc2@example.com"
                />
              </div>

              <div className="form-group">
                <label>密送 (BCC) - 用逗号分隔多个邮箱</label>
                <input
                  type="text"
                  value={formData.bcc_emails}
                  onChange={(e) => setFormData({ ...formData, bcc_emails: e.target.value })}
                  placeholder="bcc1@example.com, bcc2@example.com"
                />
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  取消
                </button>
                <button type="submit" className="btn btn-primary">
                  创建
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 发送邮件模态框 */}
      {showSendModal && selectedCampaign && (
        <div className="modal-overlay" onClick={() => !sending && setShowSendModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>发送活动: {selectedCampaign.name}</h2>
              <button 
                className="modal-close" 
                onClick={() => setShowSendModal(false)}
                disabled={sending}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSend}>
              {sending ? (
                <div className="alert alert-info">
                  <strong>🔄 发送中...</strong> 正在批量发送邮件，请不要关闭页面。这可能需要几分钟时间。
                </div>
              ) : (
                <div className="alert alert-info">
                  <strong>注意：</strong>每封邮件将单独发送给每个收件人（一对一发送）
                </div>
              )}

              <div className="form-group">
                <label>收件人邮箱 * (每行一个邮箱地址)</label>
                <textarea
                  value={sendData.recipients}
                  onChange={(e) => setSendData({ recipients: e.target.value })}
                  placeholder={'recipient1@example.com\nrecipient2@example.com\nrecipient3@example.com'}
                  style={{ minHeight: '150px' }}
                  required
                  disabled={sending}
                />
                <small style={{ color: '#666', fontSize: '12px' }}>
                  或从现有收件人中选择（总共 {recipients.length} 个）
                </small>
              </div>

              <div className="form-group">
                <label>快速选择</label>
                <div style={{ 
                  maxHeight: '150px', 
                  overflowY: 'auto', 
                  border: '1px solid #ddd', 
                  borderRadius: '5px', 
                  padding: '10px',
                  opacity: sending ? 0.5 : 1,
                  pointerEvents: sending ? 'none' : 'auto'
                }}>
                  {recipients.slice(0, 50).map((recipient) => (
                    <div key={recipient.id} style={{ marginBottom: '5px' }}>
                      <label style={{ fontWeight: 'normal', display: 'flex', alignItems: 'center', cursor: sending ? 'not-allowed' : 'pointer' }}>
                        <input
                          type="checkbox"
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSendData({
                                recipients: sendData.recipients
                                  ? `${sendData.recipients}\n${recipient.email}`
                                  : recipient.email,
                              });
                            } else {
                              setSendData({
                                recipients: sendData.recipients
                                  .split('\n')
                                  .filter((email) => email !== recipient.email)
                                  .join('\n'),
                              });
                            }
                          }}
                          style={{ marginRight: '8px' }}
                          disabled={sending}
                        />
                        <span>{recipient.email}</span>
                        {recipient.name && <span style={{ color: '#999', marginLeft: '5px' }}>({recipient.name})</span>}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowSendModal(false)}
                  disabled={sending}
                >
                  取消
                </button>
                <button 
                  type="submit" 
                  className="btn btn-success"
                  disabled={sending}
                  style={{ 
                    opacity: sending ? 0.6 : 1,
                    cursor: sending ? 'not-allowed' : 'pointer'
                  }}
                >
                  {sending ? '🔄 发送中...' : '✉️ 开始发送'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Follow-up配置模态框 */}
      {showFollowupModal && selectedCampaign && (
        <div className="modal-overlay" onClick={() => setShowFollowupModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Follow-up 配置</h2>
              <button className="modal-close" onClick={() => setShowFollowupModal(false)}>
                ×
              </button>
            </div>

            <form onSubmit={handleFollowupSubmit}>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    checked={followupData.is_enabled}
                    onChange={(e) => setFollowupData({ ...followupData, is_enabled: e.target.checked })}
                    style={{ marginRight: '8px', width: 'auto' }}
                  />
                  <span>启用自动Follow-up</span>
                </label>
              </div>

              {followupData.is_enabled && (
                <>
                  <div className="form-group">
                    <label>间隔天数</label>
                    <input
                      type="number"
                      value={followupData.interval_days}
                      onChange={(e) => setFollowupData({ ...followupData, interval_days: parseInt(e.target.value) })}
                      min="1"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>最多发送次数</label>
                    <input
                      type="number"
                      value={followupData.max_followups}
                      onChange={(e) => setFollowupData({ ...followupData, max_followups: parseInt(e.target.value) })}
                      min="1"
                      max="10"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Follow-up邮件主题</label>
                    <input
                      type="text"
                      value={followupData.subject_template}
                      onChange={(e) => setFollowupData({ ...followupData, subject_template: e.target.value })}
                      placeholder="例如: Re: {原主题}"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Follow-up邮件内容 (支持HTML)</label>
                    <textarea
                      value={followupData.content_template}
                      onChange={(e) => setFollowupData({ ...followupData, content_template: e.target.value })}
                      placeholder="输入Follow-up邮件内容"
                      style={{ minHeight: '150px' }}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={followupData.stop_on_reply}
                        onChange={(e) => setFollowupData({ ...followupData, stop_on_reply: e.target.checked })}
                        style={{ marginRight: '8px', width: 'auto' }}
                      />
                      <span>收到回复后停止</span>
                    </label>
                  </div>

                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={followupData.stop_on_connected}
                        onChange={(e) => setFollowupData({ ...followupData, stop_on_connected: e.target.checked })}
                        style={{ marginRight: '8px', width: 'auto' }}
                      />
                      <span>连上线后停止</span>
                    </label>
                  </div>
                </>
              )}

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowFollowupModal(false)}>
                  取消
                </button>
                <button type="submit" className="btn btn-primary">
                  保存配置
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 详情模态框 */}
      {showDetailModal && selectedCampaign && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
            <div className="modal-header">
              <h2>📧 Campaign 详情</h2>
              <button className="modal-close" onClick={() => setShowDetailModal(false)}>
                ×
              </button>
            </div>

            <div style={{ padding: '20px 0' }}>
              {/* 基本信息 */}
              <div style={{ marginBottom: '25px', borderBottom: '2px solid #e0e0e0', paddingBottom: '20px' }}>
                <h3 style={{ fontSize: '18px', color: '#2c3e50', marginBottom: '15px' }}>📋 基本信息</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '10px', fontSize: '14px' }}>
                  <div style={{ fontWeight: 'bold', color: '#555' }}>活动名称：</div>
                  <div>{selectedCampaign.name}</div>
                  
                  <div style={{ fontWeight: 'bold', color: '#555' }}>邮件主题：</div>
                  <div>{selectedCampaign.subject}</div>
                  
                  <div style={{ fontWeight: 'bold', color: '#555' }}>发件人：</div>
                  <div>{selectedCampaign.sender_email?.name || ''} &lt;{selectedCampaign.sender_email?.email}&gt;</div>
                  
                  {selectedCampaign.cc_emails && selectedCampaign.cc_emails.length > 0 && (
                    <>
                      <div style={{ fontWeight: 'bold', color: '#555' }}>CC：</div>
                      <div>{selectedCampaign.cc_emails.join(', ')}</div>
                    </>
                  )}
                  
                  {selectedCampaign.bcc_emails && selectedCampaign.bcc_emails.length > 0 && (
                    <>
                      <div style={{ fontWeight: 'bold', color: '#555' }}>BCC：</div>
                      <div>{selectedCampaign.bcc_emails.join(', ')}</div>
                    </>
                  )}
                  
                  <div style={{ fontWeight: 'bold', color: '#555' }}>状态：</div>
                  <div>{getStatusBadge(selectedCampaign.status)}</div>
                  
                  <div style={{ fontWeight: 'bold', color: '#555' }}>发送进度：</div>
                  <div>{selectedCampaign.sent_count}/{selectedCampaign.total_recipients}</div>
                  
                  <div style={{ fontWeight: 'bold', color: '#555' }}>创建时间：</div>
                  <div>{new Date(selectedCampaign.created_at).toLocaleString()}</div>
                  
                  {selectedCampaign.sent_at && (
                    <>
                      <div style={{ fontWeight: 'bold', color: '#555' }}>发送时间：</div>
                      <div>{new Date(selectedCampaign.sent_at).toLocaleString()}</div>
                    </>
                  )}
                </div>
              </div>

              {/* 邮件内容 */}
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '18px', color: '#2c3e50', marginBottom: '15px' }}>📝 邮件正文</h3>
                <div
                  style={{
                    border: '1px solid #ddd',
                    borderRadius: '5px',
                    padding: '20px',
                    background: '#f9f9f9',
                    minHeight: '200px',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    maxHeight: '400px',
                    overflowY: 'auto'
                  }}
                  dangerouslySetInnerHTML={{ __html: selectedCampaign.content }}
                />
              </div>

              {/* 签名 */}
              {selectedCampaign.signature && (
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ fontSize: '18px', color: '#2c3e50', marginBottom: '15px' }}>✍️ 邮件签名</h3>
                  <div style={{ marginBottom: '8px', fontSize: '13px', color: '#666' }}>
                    签名名称: <strong>{selectedCampaign.signature.name}</strong>
                  </div>
                  <div
                    style={{
                      border: '1px solid #ddd',
                      borderRadius: '5px',
                      padding: '15px',
                      background: '#fff',
                      fontSize: '13px',
                      lineHeight: '1.6'
                    }}
                    dangerouslySetInnerHTML={{ __html: selectedCampaign.signature.content }}
                  />
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-primary" onClick={() => setShowDetailModal(false)}>
                关闭
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 预览邮件模态框 */}
      {showPreviewModal && (
        <div className="modal-overlay" onClick={() => setShowPreviewModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
            <div className="modal-header">
              <h2>📄 邮件预览</h2>
              <button className="modal-close" onClick={() => setShowPreviewModal(false)}>
                ×
              </button>
            </div>

            <div style={{ padding: '20px 0' }}>
              <div className="alert alert-info">
                <strong>预览说明：</strong>这是邮件的实际显示效果，包含了选择的签名。
              </div>

              <div style={{ marginBottom: '15px' }}>
                <strong style={{ fontSize: '16px', color: '#333' }}>主题：</strong>
                <span style={{ marginLeft: '10px', fontSize: '15px' }}>{formData.subject}</span>
              </div>

              <div
                style={{
                  border: '2px solid #3498db',
                  borderRadius: '5px',
                  padding: '30px',
                  background: '#fff',
                  minHeight: '300px',
                  fontSize: '14px',
                  lineHeight: '1.6'
                }}
                dangerouslySetInnerHTML={{ __html: previewContent }}
              />
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-primary" onClick={() => setShowPreviewModal(false)}>
                关闭预览
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Campaigns;

